import { useQuery } from '@tanstack/react-query';
import { ErrorState } from '@/components/common/ErrorState';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { expenseApprovalQueryOptions } from '../api/billing.api';
import { DetailLinesSkeleton } from './DetailLinesSkeleton';
import { ExpenseItemsTable } from './ExpenseItemsTable';
import { ExpenseSummary } from './ExpenseSummary';

const ITEMS_SKELETON_COLUMN_COUNT = 5;

interface ExpenseApprovalModalProps {
  expenseId: number | null;
  isPending: boolean;
  onClose: () => void;
  onDecide: (accept: boolean) => void;
}

export function ExpenseApprovalModal({
  expenseId,
  isPending,
  onClose,
  onDecide,
}: ExpenseApprovalModalProps) {
  const { data, isLoading, error, refetch } = useQuery(expenseApprovalQueryOptions(expenseId));

  const settled = data ? data.approveStatus !== 'pending' : false;

  return (
    <Dialog
      open={expenseId !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Expense Details</DialogTitle>
        </DialogHeader>

        {error ? (
          <ErrorState
            error={error}
            fallbackTitle="Could not load this expense"
            onRetry={() => void refetch()}
            size="compact"
          />
        ) : (
          <div className="flex flex-col gap-6">
            <ExpenseSummary expense={data} />
            {isLoading || !data ? (
              <DetailLinesSkeleton columnCount={ITEMS_SKELETON_COLUMN_COUNT} />
            ) : (
              <ExpenseItemsTable lines={data.lines} />
            )}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={settled || isPending || !data}
            onClick={() => onDecide(false)}
          >
            Reject
          </Button>
          <Button
            type="button"
            disabled={settled || isPending || !data}
            onClick={() => onDecide(true)}
          >
            Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
